import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import { useStyletron } from 'parkdepot-shared/theme'
import useEmailValidation from 'parkdepot-shared/utils/hooks/useEmailValidation'
import React, { ChangeEvent, FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LinkComponent from '../Link/Link.component'

// style components
import Text from '../style/Text.style'
import {
    Title,
    FormContainer,
    InputContainer,
    InputPlaceholder,
    LoginErrorText,
    SeePasswordIcon
} from './LoginForm.style'

// components
import Notification from '../Notification/Notification.component'
import { NotificationType } from '../../types/notification.enum'

interface Props {
    onLoginClick: (e: React.FormEvent<HTMLFormElement>, email: string, password: string) => void
    loading: boolean
    setLoading: (loading: boolean) => void
    onLoginError?: string
    loginError?: boolean
}

const LoginForm: FC<Props> = ({ onLoginClick, loading, loginError }) => {
    const { t } = useTranslation()
    const validateEmail = useEmailValidation()
    const [css, theme] = useStyletron()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [hiddenPassword, setHiddenPassword] = useState(true)
    const [forgotPasswordError, setForgotPasswordError] = useState(false)
    const [isForgotPassword, setIsForgotPassword] = useState(false)
    const [forgotPasswordDisabled, setForgotPasswordDisabled] = useState(false)

    const auth0Domain = process.env.VITE_AUTH0_DOMAIN || ''
    const auth0ClientId = process.env.VITE_AUTH0_CLIENT_ID || ''

    const onForgotPasswordClick = async () => {
        if (forgotPasswordDisabled) return
        setForgotPasswordDisabled(true)
        setForgotPasswordError(false)
        if (validateEmail(email) === false) {
            setForgotPasswordError(true)
            return
        }
        setForgotPasswordError(false)
        try {
            await fetch(`https://${auth0Domain}/dbconnections/change_password`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                    client_id: auth0ClientId,
                    email: email,
                    connection: 'Username-Password-Authentication'
                })
            })
            setForgotPasswordError(false)
            setIsForgotPassword(true)
            setTimeout(() => {
                setIsForgotPassword(false)
            }, 10000)
        } catch (error) {
            setForgotPasswordError(true)
        }
    }

    return (
        <>
            <Title>
                <Text typography={theme.typography.HeadingMedium} fontWeight={700}>
                    {t('login.title')}
                </Text>
            </Title>

            <form
                autoComplete="on"
                onSubmit={(e) => {
                    e.preventDefault()
                    onLoginClick(e, email, password)
                }}
            >
                <FormContainer>
                    <div>
                        <InputPlaceholder>{t('login.email')}</InputPlaceholder>
                        <InputContainer
                            name="email"
                            data-testid="login-email-input"
                            type="email"
                            autoComplete="username"
                            value={email}
                            $error={loginError || forgotPasswordError}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setEmail(e.target.value)
                                setForgotPasswordDisabled(false)
                            }}
                            placeholder={t('login.yourEmail')}
                        />
                    </div>
                    <div
                        className={css({
                            position: 'relative'
                        })}
                    >
                        <InputPlaceholder>{t('login.password')}</InputPlaceholder>
                        <InputContainer
                            name="password"
                            data-testid="login-password-input"
                            type={hiddenPassword ? 'password' : 'text'}
                            placeholder={t('login.yourPassword')}
                            autoComplete="current-password"
                            $error={loginError}
                            value={password}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setPassword(e.target.value)
                            }}
                        />
                        <SeePasswordIcon
                            className={`wemolo-eye-${hiddenPassword ? 'closed' : 'open'}`}
                            onClick={() => setHiddenPassword(!hiddenPassword)}
                        />
                    </div>
                    {loginError && <LoginErrorText>{t('errors.loginError')}</LoginErrorText>}
                </FormContainer>
                <div
                    className={css({
                        textAlign: 'center'
                    })}
                >
                    <Button
                        id="login-btn"
                        data-testid="login-button"
                        disabled={!email || !password}
                        type="submit"
                        isLoading={loading}
                        $size={BUTTON_SIZE.medium}
                        $kind={BUTTON_KIND.primary}
                        overrides={{
                            BaseButton: {
                                style: {
                                    width: '100%'
                                }
                            }
                        }}
                    >
                        <div>{t('login.loginButton')}</div>
                    </Button>
                </div>
            </form>
            <LinkComponent
                className={css({ marginTop: '2.4rem', marginBottom: '2.4rem', display: 'block' })}
                onClick={onForgotPasswordClick}
                disabled={forgotPasswordDisabled}
            >
                {t('login.forgotPassword')}
            </LinkComponent>
            {isForgotPassword && (
                <Notification type={NotificationType.POSITIVE}>{t('login.forgotPasswordText')}</Notification>
            )}
        </>
    )
}

export default LoginForm
